// frontend/src/App.jsx

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/common/Navbar';
import Sidebar from './components/common/Sidebar';

// Auth
import Login from './components/auth/Login';
import FirstLogin from './components/auth/FirstLogin';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';

// Admin
import AdminDashboard from './components/admin/AdminDashboard';
import AdminList from './components/admin/AdminList';
import OwnerList from './components/admin/OwnerList';
import AllSimplesList from './components/admin/AllSimplesList';
import AllLogs from './components/admin/AllLogs';
import DeviceList from './components/admin/DeviceList';
import AssociationList from './components/admin/AssociationList';
import AssociationDashboard from './components/admin/AssociationDashboard';
import OwnerConsumptionDashboard from './components/admin/OwnerConsumptionDashboard';
import GlobalConsumptionDashboard from './components/admin/GlobalConsumptionDashboard';
import BillingConfigManager from './components/admin/BillingConfigManager';

// Owner
import OwnerDashboard from './components/owner/OwnerDashboard';
import OwnerAssociationList from './components/owner/OwnerAssociationList';
import SimpleList from './components/owner/SimpleList';
import OwnerLogs from './components/owner/OwnerLogs';

// Simple
import SimpleDashboard from './components/simple/SimpleDashboard';
import SimpleConsumption from './components/simple/SimpleConsumption';

// Common
import Profile from './components/common/Profile';
import Loading from './components/common/Loading';
import { ROLES } from './utils/constants';

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) return <Loading />;

  if (!user) return <Navigate to="/login" />;

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 w-full lg:w-auto overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};

const DashboardRouter = () => {
  const { user } = useAuth();

  if (user?.role === ROLES.ADMIN) {
    return <AdminDashboard />;
  } else if (user?.role === ROLES.OWNER) {
    return <OwnerDashboard />;
  } else if (user?.role === ROLES.SIMPLE) {
    return <SimpleDashboard />;
  }

  return <Navigate to="/login" />;
};

const LogsRouter = () => {
  const { user } = useAuth();
  
  if (user?.role === ROLES.ADMIN) {
    return <AllLogs />;
  } else if (user?.role === ROLES.OWNER) {
    return <OwnerLogs />;
  }
  
  return <Navigate to="/dashboard" />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Routes publiques */}
          <Route path="/login" element={<Login />} />
          <Route path="/first-login" element={<FirstLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Dashboard dynamique */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <AppLayout>
                  <DashboardRouter />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* Routes Admin */}
          <Route
            path="/admins"
            element={
              <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                <AppLayout>
                  <AdminList />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/owners"
            element={
              <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                <AppLayout>
                  <OwnerList />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/owners/:ownerId/consumption"
            element={
              <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                <AppLayout>
                  <OwnerConsumptionDashboard />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/simples"
            element={
              <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                <AppLayout>
                  <AllSimplesList />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/devices"
            element={
              <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                <AppLayout>
                  <DeviceList />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/associations"
            element={
              <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                <AppLayout>
                  <AssociationList />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/associations/:id/dashboard"
            element={
              <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.OWNER]}>
                <AppLayout>
                  <AssociationDashboard />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/consumption/global"
            element={
              <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                <AppLayout>
                  <GlobalConsumptionDashboard />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/billing-config"
            element={
              <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                <AppLayout>
                  <BillingConfigManager />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* Routes Simple */}
          <Route
            path="/my-consumption"
            element={
              <PrivateRoute allowedRoles={[ROLES.SIMPLE]}>
                <AppLayout>
                  <SimpleConsumption />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* Routes Owner */}
          <Route
            path="/my-associations"
            element={
              <PrivateRoute allowedRoles={[ROLES.OWNER]}>
                <AppLayout>
                  <OwnerAssociationList />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/my-simples"
            element={
              <PrivateRoute allowedRoles={[ROLES.OWNER]}>
                <AppLayout>
                  <SimpleList />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* Routes communes */}
          <Route
            path="/logs"
            element={
              <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.OWNER]}>
                <AppLayout>
                  <LogsRouter />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <AppLayout>
                  <Profile />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* Redirection par défaut */}
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;